import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/text'
import { api } from '@api'
import { AppTooltip } from '@components/AppTooltip'
import SetupAuthor from '@components/SetupAuthor'
import SetupDuration from '@components/SetupDuration'
import SetupPotential from '@components/SetupPotential'
import SetupRRR from '@components/SetupRRR'
import SetupTitle from '@components/SetupTitle'
import type { Tables } from '@hvf/contracts'
import { useNavigate } from '@solidjs/router'
import { createQuery } from '@tanstack/solid-query'
import { type Component, Show } from 'solid-js'
import { useService } from 'solid-services'
import { css, cx } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'
import { useCreate } from '../Create/state'
import { promptTooltipClass } from '../promptTooltipClass'
import Chip from '../Chip'
import { specColor } from '../SpecificationBadge'
import { stageColor } from '../StageBadge'
import type { EnrichedSetup } from './state'
import { SocialBadges } from './SocialBadges'
import { UpdateButton } from './UpdateButton'

const fetchThumbnail = async (path?: string | null) => {
  if (!path) return null
  if (path.startsWith('http')) return path

  const { data, error } = await api().storage.from('setup-images').createSignedUrl(path, 3600)
  return !error && data?.signedUrl ? data.signedUrl : null
}

const separatorClass = css({
  width: '1px',
  height: '14px',
  bg: 'var(--colors-editorial-hair)',
  flexShrink: 0,
})

const floatingPillBorder = css({
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--colors-editorial-hair-strong)',
})

interface ListRowProps {
  setup?: EnrichedSetup
}

const ListRow: Component<ListRowProps> = (props) => {
  const navigate = useNavigate()
  const { editSetup } = useService(useCreate)()

  const thumbnailQuery = createQuery(() => ({
    queryKey: ['setup-thumbnail', props.setup?.id, props.setup?.image_pattern],
    queryFn: () => fetchThumbnail(props.setup?.image_pattern),
    enabled: !!props.setup?.image_pattern,
    staleTime: 1000 * 60 * 50,
    gcTime: 1000 * 60 * 60,
  }))

  const greyedOutFor: Tables<'setup'>['stage'][] = ['spoiled', 'stopped-out', 'target', 'overperformance-closed', 'timed-out']
  const isgreyed = () => greyedOutFor.some((search) => search === props.setup?.stage)

  const stageLabel = () => (props.setup?.stage || 'unspecified').replaceAll('_', ' ').replaceAll('-', ' ')

  return (
    <Show when={props.setup}>
      <Grid
        gridTemplateColumns={{ base: '222px 1fr', smDown: '96px 1fr' }}
        gap={{ base: '13px', smDown: '10px' }}
        p='10px'
        bg='gray.surface.bg'
        borderRadius='14px'
        cursor='pointer'
        onClick={() => navigate(`/setups/${props.setup?.id}`)}
        class={css({
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--colors-editorial-hair)',
          filter: isgreyed() ? 'grayscale(1)' : 'none',
          transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.2s',
          _hover: {
            transform: 'translateY(-2px)',
            borderColor: 'var(--colors-editorial-hair-strong)',
            boxShadow: '0 1px 2px rgba(20,22,27,.05), 0 18px 40px -24px rgba(20,22,27,.28)',
          },
        })}
      >
        <Box
          position='relative'
          borderRadius='11px'
          overflow='hidden'
          bg='editorialSoft'
          minHeight={{ base: '122px', smDown: '86px' }}
          class={css({
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--colors-editorial-hair)',
          })}
        >
          <Box
            position='absolute'
            left={0}
            top={0}
            bottom={0}
            width='3px'
            zIndex={2}
            pointerEvents='none'
            class={css({
              bg: props.setup?.position === 'long'
                ? 'linear-gradient(180deg, var(--colors-editorial-long), transparent)'
                : 'linear-gradient(180deg, var(--colors-editorial-short), transparent)',
            })}
          />

          <Show when={thumbnailQuery.isLoading}>
            <Skeleton
              width='100%'
              height='100%'
            />
          </Show>
          <Show when={thumbnailQuery.data}>
            <Box
              position='absolute'
              inset={0}
              style={{ 'background-image': `url(${thumbnailQuery.data})` }}
              class={css({ backgroundSize: 'cover', backgroundPosition: 'center' })}
            />
          </Show>

          <AppTooltip
            delay={900}
            content='Ticker & direction'
            contentClass={promptTooltipClass}
          >
            <Flex
              position='absolute'
              top='8px'
              left='8px'
              zIndex={2}
              alignItems='center'
              gap='5px'
              bg='color-mix(in srgb, var(--colors-editorial-panel) 93%, transparent)'
              color='editorialInk'
              fontWeight='600'
              fontSize={{ base: '11px', smDown: '9.5px' }}
              px={{ base: '8px', smDown: '6px' }}
              py='4px'
              borderRadius='7px'
              boxShadow='0 1px 3px rgba(20,22,30,.12)'
              class={floatingPillBorder}
            >
              <SetupTitle
                setup={props.setup}
                fontSize='11px'
                iconSize={9}
                gap='5px'
              />
            </Flex>
          </AppTooltip>

          <AppTooltip
            delay={900}
            content='Pattern Status'
            contentClass={promptTooltipClass}
          >
            <Flex
              position='absolute'
              left='8px'
              bottom='8px'
              zIndex={2}
              alignItems='center'
              gap='6px'
              bg='color-mix(in srgb, var(--colors-editorial-panel) 93%, transparent)'
              color='editorialInk'
              fontWeight='600'
              fontSize={{ base: '10.5px', smDown: '9px' }}
              px={{ base: '8px', smDown: '6px' }}
              py='4px'
              borderRadius='7px'
              boxShadow='0 1px 3px rgba(20,22,30,.12)'
              maxW='calc(100% - 16px)'
              class={cx(floatingPillBorder, css({ textTransform: 'capitalize' }))}
            >
              <Box
                width='6px'
                height='6px'
                borderRadius='full'
                flexShrink={0}
                style={{ background: stageColor(props.setup?.stage) }}
              />
              {stageLabel()}
            </Flex>
          </AppTooltip>
        </Box>

        <Grid
          gap='8px'
          minWidth={0}
          py='2px'
          pr='2px'
        >
          <Flex
            justifyContent='space-between'
            alignItems='flex-start'
            gap='10px'
          >
            <Text
              fontSize='14px'
              fontWeight='600'
              lineHeight='1.26'
              letterSpacing='-.01em'
              color='editorialInk'
              lineClamp={2}
              minWidth={0}
            >
              {props.setup?.title}
            </Text>
            <SocialBadges
              setup={props.setup}
              gap='1'
            />
          </Flex>

          <Flex
            alignItems='center'
            gap='9px'
            flexWrap='wrap'
          >
            <Show when={props.setup?.pattern_specification}>
              <Chip
                color={specColor(props.setup?.pattern_specification)}
                tooltip='Pattern Specification'
              >
                {props.setup?.pattern_specification?.replaceAll('-', ' ')}
              </Chip>
            </Show>
            <Box class={separatorClass} />
            <AppTooltip
              delay={900}
              content='Risk Reward Ratio'
              contentClass={promptTooltipClass}
            >
              <Flex
                gap='1'
                alignItems='baseline'
              >
                <Text
                  fontSize='10px'
                  fontWeight='600'
                  letterSpacing='.08em'
                  textTransform='uppercase'
                  color='editorialInk3'
                >
                  RRR
                </Text>
                <SetupRRR
                  setup={props.setup}
                  fontSize='14px'
                />
              </Flex>
            </AppTooltip>
            <Box class={separatorClass} />
            <AppTooltip
              delay={900}
              content='Timeframe'
              contentClass={promptTooltipClass}
            >
              <SetupDuration
                setup={props.setup}
                fontSize='13px'
              />
            </AppTooltip>
            <Box class={separatorClass} />
            <SetupPotential setup={props.setup} />
          </Flex>

          <Flex
            alignItems='center'
            gap='9px'
            marginTop='auto'
          >
            <SetupAuthor
              createdBy={props.setup?.created_by}
              created_at={props.setup?.created_at}
              gap={2}
              avatarSize='30px'
              rankSize='15px'
              nameFontSize='12.5px'
              dateFontSize='10.5px'
            />
            <Box flex='1' />
            <UpdateButton
              size='rail'
              onClick={() => editSetup(props.setup as any)}
            />
          </Flex>
        </Grid>
      </Grid>
    </Show>
  )
}

export default ListRow
